/**
 * Human-visible catalogue of prompts used in the project-file batch AI pipeline (admin UX).
 * Composed from shared prompt builders kept in sync with server callers.
 */

import {
  type ProjectFileClassificationType,
  projectFileClassificationTypeSchema,
} from '../models/project-file-batch.model';
import { buildPdfGeometryPrompt } from './pdf-geometry-prompt.helpers';
import {
  buildBlueprintSegmentExtractionExtraMarkdown,
  buildNonBlueprintSegmentExtractionUserText,
  classifierSegmentHintBlockTemplate,
  projectFileExtractionUploadContextTemplateMarkdown,
} from './project-file-segment-extraction-prompt.helpers';
import { buildProjectBatchMergeInstructionBody } from './project-file-batch-merge-prompt.helpers';
import {
  buildProjectFileClassifierInstructionBody,
  projectFileClassifierFilenameBlockTemplate,
} from './project-file-classifier-prompt.helpers';
import {
  buildProjectFileClassifierStructuredOutputRootJsonSchema,
  PROJECT_FILE_CLASSIFIER_STRUCTURED_OUTPUT_NAME,
} from './project-file-classifier-json-schema.helpers';
import {
  buildDrawingDetectionRootJsonSchema,
  PROJECT_FILE_DRAWING_DETECTION_SCHEMA_NAME,
} from './project-file-drawing-detection-json-schema.helpers';
import { projectFileDrawingDetectionUserTextTemplate } from './project-file-drawing-detection-prompt.helpers';
import {
  buildBlueprintExtractionRootJsonSchema,
  getNonBlueprintSegmentStructuredOutput,
  PROJECT_FILE_BLUEPRINT_EXTRACTION_SCHEMA_NAME,
} from './project-file-extraction-json-schema.helpers';
import {
  buildProjectFileBatchMergeStructuredOutputRootJsonSchema,
  PROJECT_FILE_BATCH_MERGE_STRUCTURED_OUTPUT_NAME,
} from './project-file-batch-merge-json-schema.helpers';

export type ProjectFileBatchAiChainStructuredOutput = {
  id: string;
  /** Heading for this fragment (often the logical segment type or wrapper name). */
  label: string;
  /** `name` in OpenAI Responses `json_schema.format`. */
  structuredOutputName: string;
  /** Strict JSON Schema object passed as Responses `schema`. */
  schema: Record<string, unknown>;
};

export type ProjectFileBatchAiChainPromptSection = {
  id: string;
  /** Pipeline moment (shown as chip / caption above the card title). */
  phaseLabel: string;
  /** Short heading for this prompt block. */
  title: string;
  /** What this model call achieves in the batch pipeline (plain language). */
  purpose: string;
  /** What file bytes / structured inputs go to the LLM beside the printed instructions. */
  contextSent: readonly string[];
  /** Parsed OpenAI Responses `json_schema` definitions (readable + JSON, like admin AI tools page). */
  structuredOutputs: readonly ProjectFileBatchAiChainStructuredOutput[];
  /** Full instructional text (often includes placeholders like `{{originalFilename}}`). */
  body: string;
};

/**
 * Sections shown on Admin Project File Batches when no batch is selected (and anywhere else needed).
 */
export const getProjectFileBatchAiChainPromptSections =
  (): readonly ProjectFileBatchAiChainPromptSection[] => {
    const classifierFull = [
      projectFileClassifierFilenameBlockTemplate(),
      buildProjectFileClassifierInstructionBody(),
    ].join('\n');

    const geometryStem = buildPdfGeometryPrompt({
      projectName: '{{projectLabel}}',
      arity: 'multi',
    });

    const blueprintExtra = buildBlueprintSegmentExtractionExtraMarkdown();

    const blueprintFullUserText = buildPdfGeometryPrompt({
      projectName: '{{projectLabel}}',
      arity: 'multi',
      extraInstructions: blueprintExtra,
    });

    const nonBlueprintTypes =
      projectFileClassificationTypeSchema.options.filter(
        (t): t is Exclude<typeof t, 'blueprint'> => t !== 'blueprint',
      );

    const nonBlueprintPurposeByType: Record<
      Exclude<ProjectFileClassificationType, 'blueprint'>,
      string
    > = {
      title_block:
        'Read filing metadata from a stamp-dominated sheet — drawing id, revision, project title. The classifier fills `title_block`, so this call only runs for segments classified before it did.',
      project_description:
        'Read project identity from prose — programme / building type, location, short building description.',
      building_rendering:
        'Describe visible massing and facade/height cues from renderings or CGI for downstream modelling.',
      project_brief:
        'Extract structured requirements and design facts from förstudie / detaljplan / uppdragsunderlag — whole-building caps, named building-part materials, BTA, programs, apartment size.',
      area_schedule:
        'Read areatabell / ytsammanställning into per-zone BTA/BOA/LOA rows mapped to program types for downstream design facts.',
      bill_of_materials:
        'Not currently consumed by Nodon (mängdförteckning / materiallista) — routed to a cheap abstain.',
      cost_estimate:
        'Not currently consumed by Nodon (kostnadskalkyl / budget) — routed to a cheap abstain.',
      technical_spec:
        'Extract named building-part materials/U-values, whole-building caps, and design facts (BTA, rumshöjd, spännvidd, programs, apartment size) from teknisk beskrivning / FFU / AMA / detaljplan. Color and NCS are ignored.',
      unknown: 'Classifier could not type the segment — abstains safely.',
    };

    const buildNonBlueprintBody = (
      t: Exclude<ProjectFileClassificationType, 'blueprint'>,
    ): string =>
      buildNonBlueprintSegmentExtractionUserText({
        uploadContext: projectFileExtractionUploadContextTemplateMarkdown(),
        hintBlock: classifierSegmentHintBlockTemplate(),
        type: t,
      });

    const mergeInstruction = buildProjectBatchMergeInstructionBody();

    const mergeFull = [
      mergeInstruction,
      '',
      'Input (JSON payload):',
      '{{batchMergePayloadJson}}',
    ].join('\n');

    return [
      {
        id: 'classifier',
        phaseLabel: 'Per file · classifying',
        title: 'Document kind + segment classifier',
        purpose:
          'Identifies the overall document kind (drawing, spec, etc.) and segments the file into logical parts (title blocks, blueprints, renderings). It also extracts initial project name and description hints to guide subsequent extraction steps.',
        contextSent: [
          'Uploaded file bytes after zip/folder flatten (one inner file per row): raster images (png/jpeg/webp/gif) as vision `input_image` (`file_id`, detail high); other types as `input_file_id`. MIME resolved from extension when the browser sends a generic octet-stream.',
          'User message: the file attachment first (shared prompt-cache prefix), then original filename (`{{originalFilename}}`, relative path inside a zip/folder when flattened) plus the long classifier instruction shown below (no DB payload besides what is in the blob).',
          'One Responses scope per uploaded file (`classifyProjectFileWithOpenAi` / file-scoped conversation).',
        ],
        structuredOutputs: [
          {
            id: 'classifier',
            label: 'Root object',
            structuredOutputName:
              PROJECT_FILE_CLASSIFIER_STRUCTURED_OUTPUT_NAME,
            schema: buildProjectFileClassifierStructuredOutputRootJsonSchema(),
          },
        ],
        body: classifierFull,
      },
      {
        id: 'drawing-detection',
        phaseLabel: 'Per file · after classify',
        title: 'Drawing crop detection',
        purpose:
          'Finds each distinct drawing on the sheets (plan, sektion, fasad), with bbox, storey, scale, and building id so the pipeline can store a JPEG crop per ritning.',
        contextSent: [
          'Same uploaded file as the classifier (raster `input_image` or document `input_file_id`).',
          'Filename plus classifier building hints (`{{buildingInstanceId}}` / `{{buildingLabel}}`) so ids stay aligned.',
          'One Responses call per PDF or raster image with a `blueprint` segment or `document_kind` = `technical_drawing_set` (scope `project-file-drawing-detect|<batchId>|<fileId>`).',
        ],
        structuredOutputs: [
          {
            id: 'drawing-detection',
            label: 'Root object `{ drawings }`',
            structuredOutputName: PROJECT_FILE_DRAWING_DETECTION_SCHEMA_NAME,
            schema: buildDrawingDetectionRootJsonSchema(),
          },
        ],
        body: projectFileDrawingDetectionUserTextTemplate(),
      },
      {
        id: 'extraction-blueprint',
        phaseLabel: 'Per file · extracting (per segment)',
        title: 'Blueprint segment — geometry stem + pipeline extra block',
        purpose:
          'For each classifier segment with `classificationType = blueprint`, produces structured blueprint rows (`buildings[]`) with footprint, heights, storey data, and labels tied to classifier building_instance_id when present.',
        contextSent: [
          'Same uploaded file re-attached as in classification (raster → `input_image`, documents → `input_file_id`; one upload per file for all segment extractors on that file).',
          'User text includes geometry stem with `projectName` = storage filename or `document`, filename + document_kind block, live classifier segment hints (page, evidence, building id/label), product-scope guidance, then structured-output rules.',
          'One API call per blueprint segment row (scope `project-file-extract|<fileId>|<segmentIndex>`).',
        ],
        structuredOutputs: [
          {
            id: 'blueprint',
            label: 'Root object `{ buildings }`',
            structuredOutputName: PROJECT_FILE_BLUEPRINT_EXTRACTION_SCHEMA_NAME,
            schema: buildBlueprintExtractionRootJsonSchema(),
          },
        ],
        body: [
          '--- Geometry stem (when no extra block): ---',
          geometryStem,
          '',
          '--- Full user message for blueprint segments in batch pipeline (extra block merged): ---',
          blueprintFullUserText,
        ].join('\n'),
      },
      ...nonBlueprintTypes.map((t) => {
        const { name, schema } = getNonBlueprintSegmentStructuredOutput(t);
        return {
          id: `extraction-${t}`,
          phaseLabel: 'Per file · extracting (per segment)',
          title: `Non-blueprint segment — classificationType = ${t}`,
          purpose: nonBlueprintPurposeByType[t],
          contextSent: [
            'Same file attachment as other extractors on that file when buffer was uploaded (raster `input_image` or document `input_file_id`); empty buffer path omits attachment (text-only).',
            'User text: upload context (filename, optional document_kind + confidence) → task block → classifier segment hint lines.',
            'One API call per segment (scope `project-file-extract|<fileId>|<segmentIndex>`).',
          ],
          structuredOutputs: [
            {
              id: `extraction-${t}`,
              label: 'Root object',
              structuredOutputName: name,
              schema,
            },
          ],
          body: buildNonBlueprintBody(t),
        };
      }),
      {
        id: 'batch-merge',
        phaseLabel: 'Batch · consolidating',
        title: 'Merge labels + blueprint snapshots',
        purpose:
          'When the batch reaches consolidation, merges classifier/extraction name and description hints across files, collapses blueprint rows into canonical per-building descriptions, and resolves design_facts (one value per leaf) from candidate lists.',
        contextSent: [
          'No file attachment — text-only chat message.',
          'After the instruction block, the server appends JSON with `name_hints`, `description_hints`, `building_extraction_snapshots`, `joined_blueprint_extractions`, and `design_fact_candidates` (see `{{batchMergePayloadJson}}` below for shape).',
          'Scope `project-file-batch-label-merge|<batchId>`.',
        ],
        structuredOutputs: [
          {
            id: 'batch-merge',
            label: 'Root object',
            structuredOutputName:
              PROJECT_FILE_BATCH_MERGE_STRUCTURED_OUTPUT_NAME,
            schema: buildProjectFileBatchMergeStructuredOutputRootJsonSchema(),
          },
        ],
        body: mergeFull,
      },
    ];
  };
